import _ from 'lodash';

import {
  AudienceFuelAndConvenienceProfileTargetingAttributeFragment,
  Category_Restrictions,
  Communication_Preference_Mechanism,
  Fuel_And_Convenience_Profile_Attribute as Attribute,
  Fuel_And_Convenience_Profile_Operator,
  Offer_Activation_Operator,
} from '@occc/graphql';

import { getPointBalanceInitialValues } from '../components/FuelAndConvenienceProfile/utils/getPointBalanceInitialValues';
import { getMembershipCreatedInitialValues } from '../components/FuelAndConvenienceProfile/utils/getMembershipCreatedInitialValues';
import { getLastVisitInitialValues } from '../components/LastVisit/getLastVisitInitialValues';
import { getSurveysInitialFormValues } from '../components/Surveys/utils/getSurveysInitialFormValues';
import {
  AudienceFormInitialValues,
  FulfillmentFormValues,
  OfferActivationFormValues,
  OfferFulfillment,
} from '../types';
import { walletSteeringItemInitialValues } from '../components/WalletSteering/constants';
import { productInterestsInitialValues } from '../components/ProductInterests/constants';
import { qualifiedEventsInitialFormValues } from '../components/QualifiedEvents/constants';
import { useGetAudienceWithDataById } from '../../../dataAccessLayer/audience/useGetAudienceWithDataById';
import { getCombinedAudiencesInitialFormValues } from '../components/CombinedAudiences/utils';
import { convertQualifiedEventsToFormInitialValues } from '../components/QualifiedEvents/convertQualifiedEventsToFormInitialValues';
import { convertPurchasesToFormInitialValues } from '../components/Purchases/utils/convertPurchasesToFormInitialValues';
import { WalletSteeringItemsValues } from '../components/WalletSteering/types';
import {
  CommunicationPreferencesValues,
  EmployeeTargetingValues,
  FuelAndConvenienceProfileValues,
} from '../components/FuelAndConvenienceProfile/types';
import { HomeStoreValues } from '../components/HomeStore/types';
import { SegmentsValues } from '../components/Segments/types';
import { getExternalIdentityVerificationInitialValues } from '../components/ExternalIdentityVerification/utils';

type ACHValue = NonNullable<FuelAndConvenienceProfileValues>['hasACH'];
type Criteria = AudienceFuelAndConvenienceProfileTargetingAttributeFragment['criteria'][0];
export const getFormInitialValues = (
  audience: ReturnType<typeof useGetAudienceWithDataById>['data'],
): AudienceFormInitialValues => {
  const {
    ageWithin,
    birthday,
    fuelAndConvenienceProfile,
    purchases,
    segments,
    fulfillments,
    offerActivations,
    homeStore,
    walletSteeringItems,
    productInterests,
    qualifiedEvents,
    lastVisit,
    pointBalance,
    membershipCreated,
    employeeTargetingAttributes,
    surveyItems,
    combinedAudiences,
    externalIdentityVerification,
    communicationPreference,
  } = audience?.targetingAttributes || {};

  const ageWithinValue = ageWithin?.length
    ? {
        minimumAge: ageWithin[0].minimumAge || undefined,
        maximumAge: ageWithin[0].maximumAge || undefined,
      }
    : {};

  const criteria = fuelAndConvenienceProfile?.length ? fuelAndConvenienceProfile[0].criteria : [];
  const acquisitionChannelAttribute = criteria.filter(
    (item: Criteria) => item.attribute === Attribute.AcquisitionChannel,
  );
  const hasACHAttribute = criteria.filter((item: Criteria) => item.attribute === Attribute.HasAch);
  const eaivAttributes = criteria.filter(
    (item: Criteria) => item.attribute === Attribute.RestrictionVerification,
  );

  const eaivOperator = eaivAttributes[0]?.operator;
  const eaivVerified =
    eaivOperator === Fuel_And_Convenience_Profile_Operator.IsIn ||
    eaivOperator === Fuel_And_Convenience_Profile_Operator.IsNotIn
      ? eaivOperator
      : Fuel_And_Convenience_Profile_Operator.IsIn;

  const behaviors: FuelAndConvenienceProfileValues = {
    acquisitionChannelAttribute: acquisitionChannelAttribute[0]?.target || [],
    eaivVerified,
    eaivAttributes: (eaivAttributes[0]?.target as Category_Restrictions[]) || [],
    hasACH: (hasACHAttribute[0]?.target?.[0] as ACHValue) || '',
    activeSubBrand: criteria.find((item: Criteria) => item.attribute === Attribute.ActiveSubBrand)
      ?.target?.[0],
  };

  const purchasesValues: AudienceFormInitialValues['purchases'] =
    convertPurchasesToFormInitialValues(purchases);

  const {
    AudienceInAllSegmentsTargetingAttribute,
    AudienceInAnySegmentsTargetingAttribute,
    AudienceNotInAnySegmentsTargetingAttribute,
  } = _.groupBy(segments || [], '__typename');

  const inAllSegments = AudienceInAllSegmentsTargetingAttribute?.length
    ? _.flatten(AudienceInAllSegmentsTargetingAttribute.map((item) => item.segments))
    : [];
  const inAnySegments = AudienceInAnySegmentsTargetingAttribute?.length
    ? _.flatten(AudienceInAnySegmentsTargetingAttribute.map((item) => item.segments))
    : [];

  const notInAnySegments = AudienceNotInAnySegmentsTargetingAttribute?.length
    ? _.flatten(AudienceNotInAnySegmentsTargetingAttribute.map((item) => item.segments))
    : [];

  const segmentsValues: SegmentsValues = {
    inAllSegments,
    inAnySegments,
    notInAnySegments,
  };

  const fulfillmentValues: FulfillmentFormValues[] =
    fulfillments.length > 0
      ? fulfillments.map((item: any) => ({
          fulfilled: item.fulfilled ? OfferFulfillment.Fulfilled : OfferFulfillment.NotFulfilled,
          operator: item.operator,
          timesFulfilled: item.timesFulfilled,
          targetOffer: item.targetOffer,
        }))
      : [
          {
            fulfilled: OfferFulfillment.Fulfilled,
            targetOffer: null,
          },
        ];

  const offerActivationValues: OfferActivationFormValues[] =
    offerActivations.length > 0
      ? offerActivations.map((item) => ({
          operator: item.activationOperator,
          targetOffer: item.targetOffer,
        }))
      : [
          {
            operator: Offer_Activation_Operator.IsActivated,
            targetOffer: null,
          },
        ];

  const homeStoreValues: HomeStoreValues = {
    locations: homeStore.length > 0 ? homeStore?.[0]?.locations : [],
    locationGroups: homeStore.length > 0 ? homeStore?.[0]?.locationGroups : [],
  };

  const walletSteeringValues = (
    walletSteeringItems.length > 0
      ? walletSteeringItems.map((item) => ({
          wallet: item.wallet,
          dimension: item.attribute,
          operator: item.walletSteeringOperator,
          value: item.value,
        }))
      : [
          {
            ...walletSteeringItemInitialValues,
          },
        ]
  ) as WalletSteeringItemsValues;

  const qualifiedEventsValues = convertQualifiedEventsToFormInitialValues(qualifiedEvents);

  const communicationPreferenceCriteria = communicationPreference?.[0]?.criteria ?? [];
  const getOptInCriterion = (
    mechanism: Communication_Preference_Mechanism,
  ): NonNullable<NonNullable<CommunicationPreferencesValues>['sms']> => {
    const criterion = communicationPreferenceCriteria.find((item) => item.mechanism === mechanism);
    return {
      status: criterion?.status ?? '',
      subBrandUuid: criterion?.subBrandUuid ?? '',
    };
  };
  const communicationPreferences: CommunicationPreferencesValues = {
    sms: getOptInCriterion(Communication_Preference_Mechanism.Sms),
    push: getOptInCriterion(Communication_Preference_Mechanism.Push),
  };

  const employeeAttributesValues: EmployeeTargetingValues | undefined = employeeTargetingAttributes
    ? {
        retailerNetworkUserType: employeeTargetingAttributes.retailerNetworkUserType,
        operator: employeeTargetingAttributes.operator,
        retailerNetworks: employeeTargetingAttributes.retailerNetworks,
      }
    : undefined;

  return {
    name: audience?.name || '',
    description: audience?.description || '',
    ageWithin: ageWithinValue,
    birthdayMonths: birthday?.[0]?.birthdayMonths ?? [],
    behaviors,
    communicationPreferences,
    employeeTargetingAttributes: employeeAttributesValues,
    segments: segmentsValues,
    purchases: purchasesValues,
    fulfillments: fulfillmentValues,
    offerActivations: offerActivationValues,
    homeStore: homeStoreValues,
    walletSteeringItems: walletSteeringValues,
    productInterests: productInterests ?? {
      ...productInterestsInitialValues,
    },
    qualifiedEvents: qualifiedEventsValues ?? {
      ...qualifiedEventsInitialFormValues,
    },
    lastVisit: getLastVisitInitialValues(lastVisit),
    pointBalance: getPointBalanceInitialValues(pointBalance),
    membershipCreated: getMembershipCreatedInitialValues(membershipCreated),
    surveys: getSurveysInitialFormValues(surveyItems),
    combinedAudiences: getCombinedAudiencesInitialFormValues(combinedAudiences),
    externalIdentityVerification: getExternalIdentityVerificationInitialValues(
      externalIdentityVerification,
    ),
  };
};
